import React, { useState, useEffect } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import BudgetBarChart from './BudgetBarChart';

// Cache expiration time in milliseconds (0 hour, 28 minutes, and 28 seconds)
const CACHE_EXPIRATION = (0 * 60 * 60 * 1000) + (28 * 60 * 1000) + (28 * 1000);

// Helper function to get cache key
const getCacheKey = (userId, range) => `spending_${userId}_${range}`;

// Helper function to get cached data
const getCachedData = (key) => {
  const cached = localStorage.getItem(key);
  if (!cached) return null;
  
  const { data, timestamp } = JSON.parse(cached);
  const isExpired = Date.now() - timestamp > CACHE_EXPIRATION;
  
  return isExpired ? null : data;
};

// Helper function to cache data
const cacheData = (key, data) => {
  const cache = {
    data,
    timestamp: Date.now()
  };
  localStorage.setItem(key, JSON.stringify(cache));
};

const SpendingInsights = () => {
  const [timeRange, setTimeRange] = useState('week');
  const [spendingData, setSpendingData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [budget, setBudget] = useState(2000); // Default budget
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const { user } = useUser();

  const { getToken } = useAuth();

  // Function to fetch spending data from the API
  const fetchSpendingData = async (range, clerkUserId) => {
    // Get the base URL from environment variables or use default
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
    const endpoint = range === 'week' 
      ? `${API_BASE_URL}/spending/weekly/${clerkUserId}`
      : `${API_BASE_URL}/spending/monthly/${clerkUserId}`;
    
    try {
      // Get the auth token from Clerk
      const token = await getToken();
      
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include'  // Important for cookies if using them
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail || `Failed to fetch ${range}ly spending data: ${response.statusText}`
        );
      }
      
      return response.json();
    } catch (error) {
      console.error('API Error:', error);
      throw error; // Re-throw to be handled by the caller
    }
  };

  // Transform API data to match the expected format for BudgetBarChart
  const transformSpendingData = (apiData) => {
    // Create a copy of the array and reverse it to have the most recent data last
    const reversedData = [...apiData.data].reverse();
    
    // Set the budget from the most recent item if available
    if (reversedData.length > 0) {
      const budgetKey = apiData.time_period === 'weekly' ? 'weekly_budget' : 'monthly_budget';
      const currentBudget = reversedData[reversedData.length - 1][budgetKey];
      if (currentBudget !== undefined) {
        setBudget(currentBudget);
        // Cache the budget with the same expiration as spending data
        const budgetCache = {
          data: currentBudget,
          timestamp: Date.now()
        };
        localStorage.setItem(`budget_${user.id}_${apiData.time_period}`, JSON.stringify(budgetCache));
      }
    }
    
    return reversedData.map((item, index, array) => {
      const dateKey = apiData.time_period === 'weekly' ? 'week_start_date' : 'month_start_date';
      
      return {
        start_date: item[dateKey],
        spending: item.total_spent,
        isCurrent: index === array.length - 1
      };
    });
  };

  // Fetch data when timeRange, user, or retryCount changes
  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      
      setIsLoading(true);
      setError(null); // Reset error state on new fetch
      const cacheKey = getCacheKey(user.id, timeRange);
      
      try {
        // Try to get data from cache first
        const cachedData = getCachedData(cacheKey);
        // Try to get budget from cache
        const budgetCacheKey = `budget_${user.id}_${timeRange}`;
        const cachedBudget = getCachedData(budgetCacheKey);
        
        if (cachedData && cachedBudget) {
          setSpendingData(cachedData);
          setBudget(cachedBudget);
          setIsLoading(false);
          return;
        }
        
        // If no cache or cache expired, fetch from API
        const apiData = await fetchSpendingData(timeRange, user.id);
        const transformedData = transformSpendingData(apiData);
        
        // Update state and cache
        setSpendingData(transformedData);
        cacheData(cacheKey, transformedData);
      } catch (error) {
        console.error('Error fetching spending data:', error);
        setError({
          message: 'Failed to load spending data',
          details: error.message || 'Please check your connection and try again',
          retry: () => setRetryCount(prev => prev + 1)
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [timeRange, user, retryCount]); // Added retryCount to dependency array

  // Calculate metrics based on the selected time range
  const totalSpent = spendingData.reduce((sum, period) => sum + period.spending, 0);
  
  // Calculate average spend per period (daily for weekly view, weekly for monthly view)
  // The || 1 prevents division by zero if spendingData is empty
  const averageSpend = totalSpent / (spendingData.length || 1);

  // Calculate remaining budget
  const currentSpending = spendingData.length > 0 ? spendingData.at(-1).spending : 0;
  const remainingBudget = Math.max(0, budget - currentSpending);

  // Error state UI
  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="text-center py-12">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">{error.message}</h3>
          <p className="text-gray-500 mb-6">{error.details}</p>
          <button
            onClick={error.retry}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Loading state UI
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="animate-pulse space-y-4">
          <div className="flex justify-between items-center mb-6">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="flex space-x-2">
              <div className="h-8 bg-gray-200 rounded w-16"></div>
              <div className="h-8 bg-gray-200 rounded w-20"></div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-gray-100 rounded-xl"></div>
            ))}
          </div>
          <div className="h-64 bg-gray-100 rounded-xl"></div>
          <div className="h-4 bg-gray-100 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          Spending Trends
        </h2>
        <div className="flex space-x-2">
          <button
            onClick={() => setTimeRange('week')}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              timeRange === 'week' 
                ? 'bg-blue-100 text-blue-600 border border-blue-100' 
                : 'bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            Week
          </button>
          <button
            onClick={() => setTimeRange('month')}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              timeRange === 'month' 
                ? 'bg-blue-100 text-blue-600 border border-blue-100' 
                : 'bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            Month
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-xl">
          <p className="text-sm text-blue-600 font-medium">Spent this {timeRange}</p>
          <p className="text-2xl font-bold">${currentSpending.toFixed(0)}</p>
          <p className="text-xs text-gray-500">of ${budget.toFixed(0)} budget</p>
        </div>
        <div className="bg-green-50 p-4 rounded-xl">
          <p className="text-sm text-green-600 font-medium">
            {timeRange === 'week' ? 'Weekly' : 'Monthly'} Average
          </p>
          <p className="text-2xl font-bold">${averageSpend.toFixed(0)}</p>
          <p className="text-xs text-gray-500">
            per {timeRange === 'week' ? 'week' : 'month'}
          </p>
        </div>
        <div className="bg-purple-50 p-4 rounded-xl">
          <p className="text-sm text-purple-600 font-medium">Remaining</p>
          <p className="text-2xl font-bold">${remainingBudget.toFixed(0)}</p>
          <p className="text-xs text-gray-500">left this {timeRange}</p>
        </div>
      </div>

      {/* Chart */}
      <div className="h-80 w-full">
        <BudgetBarChart 
          data={spendingData} 
          view={timeRange} 
          budget={budget} 
        />
      </div>
    </div>
  );
};

export default SpendingInsights;
